'use client';

import styles from './SectorContexts.module.scss';
import { IconCheck, IconRocket, IconTarget, IconTrendingUp } from '@tabler/icons-react';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

const iconMap: Record<string, React.ElementType> = {
  target: IconTarget,
  rocket: IconRocket,
  chart: IconTrendingUp,
  check: IconCheck,
};

export interface SectorContext {
  title: string;
  description: string;
  gain?: string;
  icon?: 'target' | 'rocket' | 'chart' | 'check';
}

export interface SectorContextsProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  variant?: 'cards' | 'list';
  contexts?: SectorContext[];
}

export function SectorContexts({
  eyebrow = 'Contextes',
  title = 'Où nous pouvons intervenir',
  description,
  variant = 'cards',
  contexts = [],
}: SectorContextsProps) {
  useAOS();

  if (contexts.length === 0) {
    return null;
  }

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {title && <h2 className={styles.title}>{title}</h2>}
          {description && <p className={styles.description}>{description}</p>}
        </div>
        {variant === 'list' ? (
          <div
            className={styles.list}
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="150"
          >
            {contexts.map((context, index) => {
              const IconComponent = context.icon ? iconMap[context.icon] : null;
              return (
                <article
                  key={`${context.title}-${index}`}
                  className={styles.item}
                  onClick={() => gaClick('sector_context_card', { context_title: context.title })}
                >
                  <span className={styles.itemNumber}>0{index + 1}</span>
                  {IconComponent && (
                    <span className={styles.itemIcon} aria-hidden="true">
                      <IconComponent size={22} stroke={1.5} />
                    </span>
                  )}
                  <div className={styles.itemBody}>
                    <h3 className={styles.itemTitle}>{context.title}</h3>
                    <p className={styles.itemDescription}>{context.description}</p>
                  </div>
                  {context.gain && <span className={styles.itemGain}>{context.gain}</span>}
                </article>
              );
            })}
          </div>
        ) : (
          <div
            className={styles.grid}
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="150"
          >
            {contexts.map((context, index) => {
              const IconComponent = context.icon ? iconMap[context.icon] : null;
              return (
                <article
                  key={`${context.title}-${index}`}
                  className={styles.card}
                  onClick={() => gaClick('sector_context_card', { context_title: context.title })}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.number}>0{index + 1}</span>
                    {IconComponent && (
                      <span className={styles.icon} aria-hidden="true">
                        <IconComponent size={20} stroke={1.8} />
                      </span>
                    )}
                  </div>
                  <div className={styles.titleRow}>
                    <div className={styles.titleBanner}>
                      <h3 className={styles.cardTitle}>{context.title}</h3>
                    </div>
                    {context.gain && <span className={styles.gain}>{context.gain}</span>}
                  </div>
                  <p className={styles.cardDescription}>{context.description}</p>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
